/**
 * Buying journey.
 *
 * A full-screen funnel: one step at a time in the right pane, a live product
 * image in the left, and a fixed bar carrying the step markers and Continue.
 *
 * The old flow spread this across five page loads and kept state in
 * localStorage, which lost the build on a new tab and could not be shared. Here
 * the build lives in the URL, so it survives a refresh and can be sent on.
 *
 * The steps are interdependent rather than a flat list: the plan chosen in step
 * two decides how many refills step three must collect.
 */
import { addToCart, flatten, kitGroupId } from 'cart-add';
import { GIFT_PROPERTY } from 'auto-gift';
import { define } from 'custom-elements';
import {
  bestPlanOf,
  escapeAttr,
  escapeHtml,
  money,
  patchScentPicker,
  planName,
  refillsIn,
  scentPicker,
  shortTitle,
  tick,
} from 'product-shared';

/** How long a picked colour shows as chosen before the journey moves on. */
const AUTO_ADVANCE_MS = 450;

class BuyingJourney extends HTMLElement {
  connectedCallback() {
    const source = this.querySelector('[data-journey-data]');
    if (!source) return;

    try {
      this.data = JSON.parse(source.textContent);
    } catch {
      return;
    }

    this.el = {
      panel: this.querySelector('[data-journey-panel]'),
      steps: this.querySelector('[data-journey-steps]'),
      chips: this.querySelector('[data-journey-chips]'),
      back: this.querySelector('[data-journey-back]'),
      next: this.querySelector('[data-journey-continue]'),
      image: this.querySelector('[data-journey-image]'),
      caption: this.querySelector('[data-journey-caption]'),
      reviews: this.querySelector('[data-journey-reviews]'),
      build: this.querySelector('[data-journey-build]'),
      gift: this.querySelector('[data-journey-gift]'),
      scroller: this.querySelector('.funnel__panel'),
    };

    this.state = {
      caseVariant: null,
      skipCase: false,
      planId: null,
      scents: {},
      extras: [],
      // The add-on balls, as variant ids in the order they were picked. Any
      // number of colours, one of each, or none.
      ballIds: [],
    };

    // Which case product's colours are showing on the case step.
    this.caseTab = 0;

    this.active = 'case';
    this.furthest = 0;

    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('change', (event) => this.onChange(event));
    // Remembers whether the next colour change came from a tap or click, which
    // is the only kind that moves the journey on by itself (see onChange).
    this.addEventListener('pointerdown', (event) => {
      this.pointerPick = Boolean(event.target.closest('.swatch-card'));
    });
    this.addEventListener('keydown', () => {
      this.pointerPick = false;
    });
    this.addEventListener('keydown', (event) => {
      const tab = event.target.closest('[data-case-tab]');
      const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
      if (!tab || !step) return;
      const count = this.data.cases.length;
      this.caseTab = (Number(tab.dataset.caseTab) + step + count) % count;
      this.render();
      this.querySelector(`[data-case-tab="${this.caseTab}"]`)?.focus();
    });
    this.el.back.addEventListener('click', () => this.goBack());
    this.el.next.addEventListener('click', () => this.goNext());

    this.readUrl();
    this.render();
  }

  /* ---------------------------------------------------------------- model */

  get steps() {
    const steps = [
      { key: 'case', label: t('step_case') },
      { key: 'plan', label: t('step_plan') },
      { key: 'scents', label: t('step_scents') },
    ];

    // The ball is asked after the refills and before the other extras: it is
    // the one add-on whose look depends on the case already chosen.
    if (this.ballVariants.length) steps.push({ key: 'ball', label: t('step_ball') });
    if (this.data.extras.length) steps.push({ key: 'extras', label: t('step_extras') });
    // The travel bag is offered on the review step rather than a step of its own.

    steps.push({ key: 'review', label: t('step_review') });
    return steps;
  }

  get index() {
    return this.steps.findIndex((step) => step.key === this.active);
  }

  get caseVariant() {
    for (const item of this.data.cases) {
      const variant = item.variants.find((v) => String(v.id) === String(this.state.caseVariant));
      if (variant) return { ...variant, caseTitle: item.title };
    }
    return null;
  }

  /* ------------------------------------------------------------------ ball */

  /**
   * The ball is offered with a case it has been photographed in, which is
   * every Vibes colour and no Luxe one, matching the product pages, where only
   * Vibes sells it. With
   * no case chosen ("already have a case") it is offered, since a Vibes owner
   * may want one.
   */
  get ballVariants() {
    const chosen = this.caseVariant;
    if (chosen) {
      const prefix = `${flatten(chosen.title)}|`;
      if (!Object.keys(this.data.ball?.pairings || {}).some((key) => key.startsWith(prefix))) return [];
    }
    return (this.data.ball?.variants || []).filter((variant) => variant.available);
  }

  get chosenBalls() {
    return this.state.ballIds
      .map((id) => this.ballVariants.find((variant) => String(variant.id) === String(id)))
      .filter(Boolean);
  }

  /**
   * The supplied photograph of the chosen case with the most recently picked
   * ball. Pairings are sparse by design, so a combination nobody has shot yet
   * simply keeps the case photo rather than blocking the ball from being sold.
   */
  get pairingImage() {
    return this.pairingFor(this.chosenBalls.at(-1));
  }

  pairingFor(ball) {
    const chosen = this.caseVariant;
    if (!ball || !chosen) return null;

    return this.data.ball.pairings?.[`${flatten(chosen.title)}|${flatten(ball.title)}`] || null;
  }

  /** Plans are identical across scents, so the first scent represents them all. */
  get plans() {
    return this.data.scents[0]?.plans || [];
  }

  get plan() {
    return this.plans.find((plan) => plan.id === this.state.planId) || null;
  }

  /**
   * How many refills the chosen plan covers.
   *
   * Recharge exposes no count, so it is read from the plan name — "6 MONTHS -
   * 3 REFILLS" means three. A one-off purchase is a single refill. Falls back to
   * one rather than blocking the build if a plan is ever named differently.
   */
  get refillCount() {
    if (this.state.planId === 0) return 1;
    return refillsIn(this.plan);
  }

  get includesBag() {
    return Boolean(this.data.bag && this.caseVariant);
  }

  get chosenScents() {
    return Object.values(this.state.scents).reduce((total, n) => total + n, 0);
  }

  /** Can the current step be left? */
  get canAdvance() {
    switch (this.active) {
      case 'case':
        return this.state.skipCase || Boolean(this.state.caseVariant);
      case 'plan':
        return this.state.planId !== null;
      case 'scents':
        return this.chosenScents === this.refillCount;
      default:
        return true;
    }
  }

  /* --------------------------------------------------------------- events */

  onClick(event) {
    const tab = event.target.closest('[data-case-tab]');
    if (tab) {
      this.caseTab = Number(tab.dataset.caseTab);
      this.render();
      this.querySelector(`[data-case-tab="${this.caseTab}"]`)?.focus();
      return;
    }

    const planTab = event.target.closest('[data-plan-tab]');
    if (planTab) {
      const wantOnce = planTab.dataset.planTab === 'once';
      if (wantOnce) this.setPlan(0);
      else if (this.state.planId === 0 || this.state.planId === null) this.setPlan(this.bestPlan?.id ?? null);
      this.render();
      this.querySelector(`[data-plan-tab="${planTab.dataset.planTab}"]`)?.focus();
      return;
    }

    if (event.target.closest('[data-checkout]')) {
      this.add();
      return;
    }

    const jump = event.target.closest('[data-jump]');
    if (jump) {
      this.active = jump.dataset.jump;
      this.render();
      return;
    }

    const skip = event.target.closest('[data-skip]');
    if (skip) {
      if (this.active === 'case') {
        this.state.skipCase = true;
        this.state.caseVariant = null;
      }
      if (this.active === 'ball') this.state.ballIds = [];
      if (this.active === 'extras') this.state.extras = [];
      this.goNext();
      return;
    }

    const step = event.target.closest('[data-journey-scent-step]');
    if (step) {
      this.adjustScent(step.dataset.variant, Number(step.dataset.journeyScentStep));
      return;
    }

    const chip = event.target.closest('[data-chip-remove]');
    if (chip) this.adjustScent(chip.dataset.chipRemove, -1);
  }

  onChange(event) {
    const { name, value, checked } = event.target;

    if (name === 'journey-case') {
      this.state.caseVariant = value;
      this.state.skipCase = false;
      this.render();

      // One colour is the whole of this step, so picking one moves straight on
      // to the plan, after a beat that lets the chosen outline register.
      // Continue still works for anyone who waits. Only a pointer pick does
      // this: arrow keys walk the radios one change at a time, and jumping on
      // the first would take a keyboard user off the step mid-browse.
      if (this.pointerPick) {
        const chosen = value;
        clearTimeout(this.advanceTimer);
        this.advanceTimer = setTimeout(() => {
          if (this.active === 'case' && String(this.state.caseVariant) === String(chosen)) this.goNext();
        }, AUTO_ADVANCE_MS);
      }
      this.pointerPick = false;
      return;
    }

    if (name === 'journey-plan') {
      this.setPlan(value === 'once' ? 0 : Number(value));
      this.render();
      return;
    }

    if (name === 'journey-ball') {
      this.state.ballIds = checked
        ? [...this.state.ballIds, value]
        : this.state.ballIds.filter((id) => id !== value);
      this.render();
      return;
    }

    if (name === 'journey-extra') {
      this.state.extras = checked
        ? [...this.state.extras, value]
        : this.state.extras.filter((id) => id !== value);
      this.render();
      return;
    }
  }

  adjustScent(id, delta) {
    const current = this.state.scents[id] || 0;
    const wanted = current + delta;

    // Never let the selection exceed what the plan covers.
    if (wanted < 0) return;
    if (delta > 0 && this.chosenScents >= this.refillCount) return;

    if (wanted === 0) delete this.state.scents[id];
    else this.state.scents[id] = wanted;

    this.render({ scentsOnly: true });
  }

  goNext() {
    if (this.active === 'review') {
      this.add();
      return;
    }
    if (!this.canAdvance) return;

    const next = this.steps[this.index + 1];
    this.furthest = Math.max(this.furthest, this.index + 1);
    if (next) this.active = next.key;
    this.render();
  }

  goBack() {
    const previous = this.steps[this.index - 1];
    if (previous) {
      this.active = previous.key;
      this.render();
    }
  }

  /* --------------------------------------------------------------- render */

  /**
   * Draws the current step, the step markers, the chips and the image pane.
   *
   * @param {object} [options]
   * @param {boolean} [options.scentsOnly] - Only scent counts have changed.
   */
  render({ scentsOnly = false } = {}) {
    // A scent count changes nothing on the scent step but the tiles' numbers,
    // so those are set on the tiles already there. Redrawing the panel would
    // recreate every photo, which a customer sees as a flicker, and the button
    // just pressed, which loses keyboard focus. Tiles that no longer match are
    // drawn afresh, as is every other step.
    const patched = scentsOnly && this.active === 'scents' && patchScentPicker(this.el.panel, this.scentStepData);
    if (!patched) this.el.panel.innerHTML = this.panel();
    this.renderSteps();
    this.renderChips();
    this.renderMedia();

    this.el.back.hidden = this.index === 0;
    this.el.next.textContent = this.active === 'review' ? t('checkout') : t('continue');
    if (this.el.reviews) this.el.reviews.hidden = this.active !== 'review';
    this.el.next.disabled = !this.canAdvance;

    // A new step opens at its top. Redraws within a step, such as a scent
    // count going up, leave the customer where they were.
    if (this.active !== this.renderedStep) {
      this.el.scroller.scrollTop = 0;
      this.renderedStep = this.active;
    }
    this.syncUrl();
  }

  panel() {
    const heading = `<h2 class="funnel__heading">${escapeHtml(t(`heading_${this.active}`))}</h2>`;

    switch (this.active) {
      case 'case':
        return heading + this.ticks() + this.caseStep();
      case 'plan':
        return heading + this.planStep();
      case 'scents':
        return heading + this.scentStep();
      case 'ball':
        return heading + this.ballStep();
      case 'extras':
        return heading + this.extraStep();
      default:
        return heading + this.reviewStep();
    }
  }

  ticks() {
    if (!this.data.ticks.length) return '';
    return `<ul class="funnel__ticks" role="list">${this.data.ticks
      .map((text) => `<li class="funnel__tick">${tick(16)}${escapeHtml(text)}</li>`)
      .join('')}</ul>`;
  }

  /**
   * One tab per case product (Luxe, Vibes), each showing only its own colours.
   * The cases differ a lot in price, so mixing their colours in one grid would
   * hide which case a colour belongs to.
   */
  caseStep() {
    const cases = this.data.cases;

    // Open on the tab holding the chosen colour, the first time round.
    if (this.state.caseVariant && !this.caseTabSet) {
      const found = cases.findIndex((item) =>
        item.variants.some((v) => String(v.id) === String(this.state.caseVariant))
      );
      if (found > -1) this.caseTab = found;
    }
    this.caseTabSet = true;

    const current = cases[this.caseTab] || cases[0];

    const tabs =
      cases.length > 1
        ? `<div class="funnel__tabs" role="tablist">${cases
            .map((item, i) => {
              const selected = item === current;
              const from = Math.min(...item.variants.map((v) => v.price));
              return `
                <button class="funnel__tab" type="button" role="tab" data-case-tab="${i}"
                        aria-selected="${selected}" tabindex="${selected ? 0 : -1}">
                  ${escapeHtml(shortTitle(item.title))}
                  <span class="funnel__tab-price">${money(from)}</span>
                </button>`;
            })
            .join('')}</div>`
        : '';

    const cards = current.variants
      .map((variant) => {
        const chosen = String(this.state.caseVariant) === String(variant.id);
        // The case's own photograph reads better than a flat colour chip, so
        // the swatch metafield is only the fallback.
        const dot = variant.image
          ? `background-image: url(${escapeAttr(variant.image)})`
          : `background: ${escapeAttr(variant.swatch || 'transparent')}`;

        return `
          <label class="swatch-card ${chosen ? 'swatch-card--chosen' : ''}">
            <input class="visually-hidden" type="radio" name="journey-case" value="${variant.id}"
                   ${chosen ? 'checked' : ''} ${variant.available ? '' : 'disabled'}>
            <span class="swatch-card__dot" style="${dot}"></span>
            <span class="swatch-card__name">${escapeHtml(variant.title)}</span>
            ${chosen ? `<span class="swatch-card__tick">${tick(12)}</span>` : ''}
          </label>`;
      })
      .join('');

    return `
      <div class="funnel__choices">
        ${tabs}
        <div class="funnel__grid funnel__grid--cases" role="${cases.length > 1 ? 'tabpanel' : 'group'}">${cards}</div>
        <p class="funnel__aside">
          <button class="funnel__link" type="button" data-skip>${escapeHtml(t('already_have'))}</button>
        </p>
      </div>`;
  }

  /** The plan with the most refills, which is also the cheapest per refill. */
  get bestPlan() {
    return bestPlanOf(this.plans);
  }

  setPlan(id) {
    if (this.state.planId === id) return;
    this.state.planId = id;
    // A different plan covers a different number of refills, so start over.
    this.state.scents = {};
  }

  /**
   * Subscribe and one-off sit on separate tabs, and the page opens on
   * subscribe with the best-value plan already chosen, because subscribing is
   * the outcome the business wants. One-off is still one click away.
   */
  planStep() {
    if (this.state.planId === null && this.bestPlan) this.setPlan(this.bestPlan.id);

    const casePrice = this.caseVariant?.price || 0;
    const fullPrice = this.data.scents[0]?.price || 0;
    const oneOff = this.state.planId === 0;

    const tabs = this.plans.length
      ? `<div class="funnel__tabs" role="tablist">
          <button class="funnel__tab" type="button" role="tab" data-plan-tab="subscribe" aria-selected="${!oneOff}">
            ${escapeHtml(t('recommended'))}
          </button>
          <button class="funnel__tab" type="button" role="tab" data-plan-tab="once" aria-selected="${oneOff}">
            ${escapeHtml(t('one_off'))}
          </button>
        </div>`
      : '';

    const card = ({ id, name, unit, count, badge }) => {
      const chosen = id === 'once' ? oneOff : this.state.planId === id;
      const total = casePrice + unit * count;
      const was = casePrice + fullPrice * count;
      const saving = fullPrice > unit ? Math.round((1 - unit / fullPrice) * 100) : 0;
      const meta = `${t('case_and')} ${count} ${count === 1 ? t('refill_one') : t('refill_many')}`;

      return `
        <label class="plan-card ${chosen ? 'plan-card--chosen' : ''}">
          <input class="visually-hidden" type="radio" name="journey-plan" value="${id}" ${chosen ? 'checked' : ''}>
          <span class="plan-card__radio" aria-hidden="true"></span>
          <span class="plan-card__main">
            <span class="plan-card__name">
              ${escapeHtml(name)}
              ${badge ? `<span class="plan-card__badge">${escapeHtml(badge)}</span>` : ''}
            </span>
            <span class="plan-card__meta">${escapeHtml(meta)}${
              id === 'once' ? '' : ` · ${escapeHtml(t('per_refill').replace('{{ price }}', money(unit)))}`
            }</span>
          </span>
          <span class="plan-card__price">
            ${money(total)}
            ${saving ? `<s class="plan-card__was">${money(was)}</s>` : ''}
            ${saving ? `<span class="plan-card__saving">${escapeHtml(t('save_percent').replace('{{ percent }}', saving))}</span>` : ''}
          </span>
        </label>`;
    };

    const best = this.bestPlan;
    const cards = oneOff
      ? card({ id: 'once', name: t('one_off'), unit: fullPrice, count: 1 }) +
        `<p class="funnel__note">${escapeHtml(t('one_off_note'))}</p>`
      : this.plans
          .map((plan) =>
            card({
              id: plan.id,
              name: planName(plan.name),
              unit: plan.price,
              count: refillsIn(plan),
              badge: plan === best && this.plans.length > 1 ? t('best_value') : '',
            })
          )
          .join('');

    const benefits = !oneOff && this.data.benefits.length
      ? `<ul class="funnel__benefits" role="list">${this.data.benefits
          .map((b) => `<li class="funnel__benefit">${tick(14)}${escapeHtml(b)}</li>`)
          .join('')}</ul>`
      : '';

    return `<div class="funnel__choices">${tabs}<div class="funnel__plans">${cards}</div>${benefits}</div>`;
  }

  /**
   * The same tiles and meter as the kit and refill builders. Theirs leave the
   * meter off a set of one; here it stays for a one-off's single refill too,
   * so the number to choose reads the same way whichever plan led here.
   */
  scentStep() {
    return `<div class="funnel__choices">${scentPicker(this.scentStepData)}</div>`;
  }

  /**
   * What the scent step is drawn from, and patched from (see render). Each
   * scent carries its `available` flag, so a sold-out scent cannot be added,
   * as in the other builders, rather than being refused at checkout.
   */
  get scentStepData() {
    return {
      scents: this.data.scents.map((scent) => ({ ...scent, quantity: this.state.scents[scent.id] || 0 })),
      needed: this.refillCount,
      meterFrom: 1,
      stepAttribute: 'data-journey-scent-step',
      id: 'funnel-tally',
      strings: {
        scentsLeft: t('scents_left'),
        scentsDone: t('scents_done'),
        less: t('less'),
        more: t('more'),
      },
    };
  }

  /**
   * The add-on balls: tick as many colours as wanted, or none.
   *
   * Each card shows its ball in the chosen case when that pairing was shot,
   * as the kit builder's cards do, so the row reads as the customer's own case
   * with each ball in it. An unshot pairing falls back to the ball's own photo.
   */
  ballStep() {
    const cards = this.ballVariants
      .map((ball) => ({ ...ball, image: this.pairingFor(ball) || ball.image }))
      .map((ball) => {
        const chosen = this.state.ballIds.includes(String(ball.id));
        const dot = ball.image
          ? `background-image: url(${escapeAttr(ball.image)})`
          : ball.swatch
            ? `background-color: ${escapeAttr(ball.swatch)}`
            : '';

        return `
          <label class="swatch-card ${chosen ? 'swatch-card--chosen' : ''}">
            <input class="visually-hidden" type="checkbox" name="journey-ball" value="${escapeAttr(ball.id)}" ${chosen ? 'checked' : ''}>
            <span class="swatch-card__dot" style="${dot}"></span>
            <span class="swatch-card__name">
              ${escapeHtml(ball.title)} · ${money(ball.price)}
            </span>
            ${chosen ? `<span class="swatch-card__tick">${tick(12)}</span>` : ''}
          </label>`;
      })
      .join('');

    return `
      <div class="funnel__choices">
        <div class="funnel__grid">${cards}</div>
        <p class="funnel__aside">
          <button class="funnel__link" type="button" data-skip>${escapeHtml(t('skip'))}</button>
        </p>
      </div>`;
  }

  /**
   * A single extra is drawn as one wide card, picture beside the name, rather
   * than a lone square in a grid built for three.
   */
  extraStep() {
    const single = this.data.extras.length === 1;
    const cards = this.data.extras
      .map((extra) => {
        const chosen = this.state.extras.includes(String(extra.id));
        return `
          <label class="swatch-card ${single ? 'swatch-card--row' : ''} ${chosen ? 'swatch-card--chosen' : ''}">
            <input class="visually-hidden" type="checkbox" name="journey-extra" value="${extra.id}" ${chosen ? 'checked' : ''}>
            <span class="swatch-card__dot" style="background-image: url(${escapeAttr(extra.image || '')})"></span>
            <span class="swatch-card__name">
              ${escapeHtml(extra.title)}<span class="swatch-card__price">${single ? '' : ' · '}${money(extra.price)}</span>
            </span>
            ${chosen ? `<span class="swatch-card__tick">${tick(12)}</span>` : ''}
          </label>`;
      })
      .join('');

    return `
      <div class="funnel__choices">
        <div class="funnel__grid ${single ? 'funnel__grid--single' : ''}">${cards}</div>
        <p class="funnel__aside">
          <button class="funnel__link" type="button" data-skip>${escapeHtml(t('skip'))}</button>
        </p>
      </div>`;
  }

  /**
   * The cart keeps one line per scent (each is its own variant), but the review
   * reads them as one refills line with the mix underneath. The travel bag is
   * included free with a case.
   */
  reviewStep() {
    const lines = this.lines;
    // Each line leads with a photo of what it is. A mix of refills shows two
    // of its scents stacked corner to corner in the same square, so every
    // title still starts at the same place.
    const thumbs = (images) => {
      const shown = [...new Set(images.filter(Boolean))].slice(0, 2);
      return `<span class="review-line__thumbs" aria-hidden="true">${shown
        .map((src) => `<img class="review-line__thumb" src="${escapeAttr(src)}" alt="" width="56" height="56" loading="lazy">`)
        .join('')}</span>`;
    };
    const row = (title, detail, price, was, images = []) => `
      <div class="review-line">
        ${thumbs(images)}
        <span class="review-line__title">
          ${escapeHtml(title)}
          ${detail ? `<span class="review-line__plan">${escapeHtml(detail)}</span>` : ''}
        </span>
        <span class="review-line__price">
          ${was ? `<s class="review-line__was">${money(was)}</s>` : ''}${price}
        </span>
      </div>`;

    const caseLine = lines.find((line) => line.kind === 'case');
    const refillLines = lines.filter((line) => line.kind === 'refill');
    // Balls are reviewed with the extras: a line each, at its own price.
    const otherLines = lines.filter((line) => line.kind === 'ball' || line.kind === 'extra');
    const bagLine = lines.find((line) => line.kind === 'bag');

    let html = caseLine ? row(caseLine.title, '', money(caseLine.price), null, [caseLine.image]) : '';

    if (refillLines.length) {
      const count = refillLines.reduce((sum, line) => sum + line.quantity, 0);
      const cost = refillLines.reduce((sum, line) => sum + line.price * line.quantity, 0);
      const full = refillLines.reduce((sum, line) => sum + line.fullPrice * line.quantity, 0);
      const mix = refillLines.map((line) => `${line.quantity} ${line.title}`).join(' · ');
      const plan = refillLines[0].planName ? planName(refillLines[0].planName) : t('one_off');
      html += row(
        `${t('refills')} ×${count}`,
        `${mix}, ${plan}`,
        money(cost),
        full > cost ? full : null,
        refillLines.map((line) => line.image),
      );
    }

    otherLines.forEach((line) => (html += row(line.title, '', money(line.price), null, [line.image])));

    if (bagLine) html += row(bagLine.title, '', escapeHtml(t('free')), bagLine.fullPrice, [bagLine.image]);

    const total = lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
    const saving = lines.reduce((sum, line) => sum + ((line.fullPrice ?? line.price) - line.price) * line.quantity, 0);
    const stock = t('stock');

    // The error line's fallback is what addToCart shows when the cart gives no
    // reason. It is set as text, not markup, so Liquid passes it through `t`
    // alone (which already escapes once) and it is escaped here only for the
    // attribute; a second `escape` in Liquid showed the customer "couldn&#39;t".
    return `
      <div class="funnel__choices">
        ${html}
        <p class="review-total"><span>${escapeHtml(t('total'))}</span><strong>${money(total)}</strong></p>
        ${saving > 0 ? `<p class="review-saving">${escapeHtml(t('you_save').replace('{{ amount }}', money(saving)))}</p>` : ''}
        <button class="button button--primary funnel__checkout" type="button" data-checkout>${escapeHtml(t('checkout'))}</button>
        <p class="funnel__error" role="alert" data-journey-error data-fallback="${escapeAttr(t('add_failed'))}" hidden></p>
        ${stock && stock !== 'stock' ? `<p class="funnel__stock"><span class="funnel__stock-dot" aria-hidden="true"></span>${escapeHtml(stock)}</p>` : ''}
      </div>`;
  }

  renderSteps() {
    this.el.steps.innerHTML = this.steps
      .map((step, i) => {
        let modifier = '';
        if (i === this.index) modifier = 'funnel__step--current';
        else if (i <= this.furthest) modifier = 'funnel__step--done';

        const done = modifier === 'funnel__step--done';

        return `
          <li class="funnel__step ${modifier}">
            <span class="funnel__step-number">${i + 1}</span>
            <button class="funnel__step-label" type="button" ${done ? `data-jump="${step.key}"` : 'disabled'}>
              ${escapeHtml(step.label)}
            </button>
          </li>`;
      })
      .join('');
  }

  /** Chosen scents appear as removable thumbnails, so the build stays visible. */
  renderChips() {
    const chips = Object.entries(this.state.scents).flatMap(([id, qty]) => {
      const scent = this.data.scents.find((s) => String(s.id) === String(id));
      if (!scent) return [];

      return Array.from({ length: qty }, (_, n) => `
        <li class="funnel__chip">
          <img src="${escapeAttr(scent.image || '')}" alt="${escapeAttr(scent.title)}" width="40" height="40">
          <button class="funnel__chip-remove" type="button" data-chip-remove="${id}"
                  aria-label="${escapeAttr(t('remove'))} ${escapeAttr(scent.title)}">×</button>
        </li>`);
    });

    this.el.chips.innerHTML = chips.join('');
  }

  /**
   * The image pane builds up with the customer's choices: the chosen case
   * colour, then the plan, then a thumbnail per refill. The free travel bag
   * has its own badge in the corner, so it doesn't read as one of the refills.
   */
  renderMedia() {
    const variant = this.caseVariant;
    // A shot of the chosen case and ball together outranks the case's own: it
    // is what the customer is actually buying.
    const image =
      this.pairingImage ||
      variant?.image ||
      this.data.cases[this.caseTab]?.variants[0]?.image ||
      this.data.cases[0]?.variants[0]?.image;

    if (image) this.el.image.setAttribute('src', image);
    else this.el.image.removeAttribute('src');

    this.el.caption.textContent = variant ? `${shortTitle(variant.caseTitle)} · ${variant.title}` : '';

    if (!this.el.build) return;

    const planLabel =
      this.state.planId === 0 ? t('one_off') : this.plan ? planName(this.plan.name) : '';

    const refills = Object.entries(this.state.scents).flatMap(([id, qty]) => {
      const scent = this.data.scents.find((s) => String(s.id) === String(id));
      if (!scent) return [];
      return Array.from(
        { length: qty },
        () => `<li class="funnel__build-item"><img src="${escapeAttr(scent.image || '')}" alt="${escapeAttr(scent.title)}" width="48" height="48"></li>`
      );
    });

    this.el.build.innerHTML = `
      ${planLabel ? `<p class="funnel__build-plan">${escapeHtml(planLabel)}</p>` : ''}
      ${refills.length ? `<ul class="funnel__build-items" role="list">${refills.join('')}</ul>` : ''}`;

    this.renderGift();
  }

  /** The free travel bag, shown from the moment a case is chosen. */
  renderGift() {
    if (!this.el.gift) return;

    // Built once rather than on every redraw, so the photo doesn't flicker.
    if (this.includesBag && !this.el.gift.childElementCount) {
      this.el.gift.innerHTML = `
        <img class="funnel__gift-image" src="${escapeAttr(this.data.bag.image || '')}" alt="" width="56" height="56">
        <span class="funnel__gift-text">
          <strong>${escapeHtml(t('bag_badge'))}</strong>
          <span>${escapeHtml(t('bag_badge_note'))}</span>
        </span>`;
    }
    this.el.gift.hidden = !this.includesBag;
  }

  /* ---------------------------------------------------------------- lines */

  get lines() {
    const lines = [];

    if (this.caseVariant) {
      lines.push({
        kind: 'case',
        id: this.caseVariant.id,
        title: `${this.caseVariant.caseTitle} · ${this.caseVariant.title}`,
        price: this.caseVariant.price,
        quantity: 1,
        image: this.caseVariant.image,
      });
    }

    Object.entries(this.state.scents).forEach(([id, quantity]) => {
      const scent = this.data.scents.find((s) => String(s.id) === String(id));
      if (!scent) return;

      const allocation = scent.plans.find((plan) => plan.id === this.state.planId);
      lines.push({
        kind: 'refill',
        id: scent.id,
        title: scent.title,
        fullPrice: scent.price,
        price: allocation ? allocation.price : scent.price,
        quantity,
        sellingPlan: allocation ? allocation.id : null,
        planName: allocation ? allocation.name : null,
        image: scent.image,
      });
    });

    this.chosenBalls.forEach((ball) => {
      lines.push({
        kind: 'ball',
        id: ball.id,
        title: `${this.data.ball.title} · ${ball.title}`,
        price: ball.price,
        quantity: 1,
        image: ball.image,
      });
    });

    this.state.extras.forEach((id) => {
      const extra = this.data.extras.find((e) => String(e.id) === String(id));
      if (extra) {
        lines.push({ kind: 'extra', id: extra.id, title: extra.title, price: extra.price, quantity: 1, image: extra.image });
      }
    });

    // The travel bag comes free with every case. It goes in the cart marked as
    // the gift line, the same property the theme's auto-gift uses.
    if (this.includesBag) {
      lines.push({
        kind: 'bag',
        id: this.data.bag.id,
        title: this.data.bag.title,
        fullPrice: this.data.bag.price,
        price: 0,
        quantity: 1,
        image: this.data.bag.image,
        properties: { [GIFT_PROPERTY]: 'true' },
      });
    }

    return lines;
  }

  /* ------------------------------------------------------------------ url */

  syncUrl() {
    const url = new URL(window.location.href);
    const set = (key, value) => (value ? url.searchParams.set(key, value) : url.searchParams.delete(key));

    set('case', this.state.skipCase ? 'skip' : this.state.caseVariant);
    set('plan', this.state.planId === 0 ? 'once' : this.state.planId);
    set('scents', Object.entries(this.state.scents).map(([id, n]) => `${id}:${n}`).join(','));
    set('extras', this.state.extras.join(','));
    set('balls', this.state.ballIds.join(','));
    url.searchParams.delete('ball');
    url.searchParams.delete('bag');

    window.history.replaceState({}, '', url);
  }

  /** Restore a shared build, ignoring anything that no longer exists. */
  readUrl() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('case') && !params.has('plan')) return;

    const caseParam = params.get('case');
    if (caseParam === 'skip') this.state.skipCase = true;
    else if (caseParam) {
      const exists = this.data.cases.some((item) =>
        item.variants.some((v) => String(v.id) === caseParam)
      );
      if (exists) this.state.caseVariant = caseParam;
    }

    const planParam = params.get('plan');
    if (planParam === 'once') this.state.planId = 0;
    else if (planParam && this.plans.some((plan) => plan.id === Number(planParam))) {
      this.state.planId = Number(planParam);
    }

    (params.get('scents') || '').split(',').forEach((pair) => {
      const [id, n] = pair.split(':');
      if (this.data.scents.some((s) => String(s.id) === id) && Number(n) > 0) {
        this.state.scents[id] = Number(n);
      }
    });

    // Trim anything the restored plan cannot cover.
    while (this.chosenScents > this.refillCount) {
      const key = Object.keys(this.state.scents)[0];
      this.state.scents[key] -= 1;
      if (this.state.scents[key] === 0) delete this.state.scents[key];
    }

    (params.get('extras') || '')
      .split(',')
      .filter(Boolean)
      .forEach((id) => {
        if (this.data.extras.some((e) => String(e.id) === id)) this.state.extras.push(id);
      });

    // `ball` is the single-ball parameter from before several could be picked.
    (params.get('balls') || params.get('ball') || '')
      .split(',')
      .filter(Boolean)
      .forEach((id) => {
        if (this.ballVariants.some((v) => String(v.id) === id) && !this.state.ballIds.includes(id)) {
          this.state.ballIds.push(id);
        }
      });


    // A restored build is complete, so open on the review step.
    this.active = 'review';
    this.furthest = this.steps.length - 1;
  }

  /* ------------------------------------------------------------------ add */

  /**
   * The build goes to the cart in one request and the customer goes to
   * checkout. The add itself is the shared one, so this path earns the free
   * gift and moves the header badge like every other.
   */
  async add() {
    const groupId = kitGroupId();

    const items = this.lines.map((line) => ({
      id: line.id,
      quantity: line.quantity,
      properties: { _journey: groupId, ...(line.properties || {}) },
      ...(line.sellingPlan ? { selling_plan: line.sellingPlan } : {}),
    }));

    if (!items.length) return;

    this.el.next.disabled = true;
    const checkout = this.querySelector('[data-checkout]');
    if (checkout) checkout.disabled = true;

    try {
      // No drawer: the next thing the customer sees is checkout.
      const added = await addToCart({
        items,
        openDrawer: false,
        errorTarget: this.querySelector('[data-journey-error]'),
      });
      if (added) window.location.href = `${window.Shopify.routes.root}checkout`;
    } finally {
      this.el.next.disabled = false;
      if (checkout) checkout.disabled = false;
    }
  }
}

/**
 * Strings come from Liquid via data attributes, which the DOM exposes in
 * camelCase, so snake_case keys are converted before lookup.
 */
function t(key) {
  const camel = key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  return document.querySelector('[data-journey-strings]')?.dataset[camel] || key;
}

define('buying-journey', BuyingJourney);
